'use client'

import { Loader2 } from 'lucide-react'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type {
  AdminUserSummary,
  UserAuditEventType,
  UserAuditLogEntry,
  UserChangeSource,
} from '@/features/admin/api/admin-users.api'
import { useUserAuditLog } from '@/features/admin/hooks/use-admin-users'
import { formatDateTime } from '@/lib/format/date'

const EVENT_LABEL: Record<UserAuditEventType, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  ROLE_CHANGED: 'Role changed',
  DEACTIVATED: 'Deactivated',
  SYNC_FAILED: 'Sync failed',
}

const SOURCE_LABEL: Record<UserChangeSource, string> = {
  WEBHOOK: 'Clerk webhook',
  CRON: 'Daily sync',
  JIT: 'On sign-in',
  MANUAL_SYNC: 'Manual sync',
  ADMIN_ACTION: 'Staff/admin action',
  SELF_SERVICE: 'Self-service',
}

function display(value: unknown): string {
  if (value === null || value === undefined) return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

/** "role: investor → staff" when both sides have the field, else "email: a@x.com". */
function describeChanges(entry: UserAuditLogEntry): string[] {
  const after = entry.after ?? {}
  const before = entry.before ?? {}
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
  return keys.map((key) =>
    key in before && key in after
      ? `${key}: ${display(before[key])} → ${display(after[key])}`
      : `${key}: ${display(after[key] ?? before[key])}`,
  )
}

/** Read-only trail of every change made to one user's mirrored account (admin-only endpoint). */
export function UserAuditLogDialog({
  user,
  open,
  onOpenChange,
}: {
  user: AdminUserSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data, isLoading, isError, error, refetch } = useUserAuditLog(user?.id ?? null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Account activity</DialogTitle>
          <DialogDescription>
            {user?.name ?? user?.email} — changes made to this account, newest first.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Loader2
            className="text-muted-foreground mx-auto my-8 size-6 animate-spin"
            aria-label="Loading activity"
          />
        ) : isError ? (
          <QueryErrorState error={error} onRetry={() => void refetch()} compact />
        ) : !data || data.events.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">No activity recorded.</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {data.events.map((entry) => (
              <li key={entry.id} className="border-border rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={entry.eventType === 'SYNC_FAILED' ? 'destructive' : 'outline'}>
                    {EVENT_LABEL[entry.eventType]}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {SOURCE_LABEL[entry.source]}
                    {/* Self-service is the user changing their own account — "by <themselves>" adds nothing. */}
                    {entry.actorName && entry.source !== 'SELF_SERVICE'
                      ? ` · by ${entry.actorName}`
                      : ''}
                  </span>
                  <span className="text-muted-foreground ml-auto text-xs">
                    {formatDateTime(entry.createdAt)}
                  </span>
                </div>
                {entry.errorMessage ? (
                  <p className="text-destructive mt-2 break-words text-xs">{entry.errorMessage}</p>
                ) : null}
                {describeChanges(entry).map((line) => (
                  <p key={line} className="text-muted-foreground mt-1 break-words text-xs">
                    {line}
                  </p>
                ))}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  )
}
